import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { User, Mail, Briefcase, FileText, Save } from 'lucide-react'
import toast from 'react-hot-toast'
import { useSettingsStore } from '@/store/settings.store'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useAuthStore } from '@/store/auth.store'
import { employeesService } from '@/services/employeesService'
import {
  GenderLookup,
  DepartmentLookup,
  CountryLookup,
  ProfessionIqamaLookup,
  NoticePeriodLookup,
  MaritalStatusLookup,
  JobLookup,
} from '@/api/types'

// Validation schema
const employeeSchema = z.object({
  // Personal Information - English
  EmpFirstNameEn: z.string().min(1, 'First name (English) is required'),
  EmpLastNameEn: z.string().min(1, 'Last name (English) is required'),
  EmpSecondNameEn: z.string().optional(),
  EmpFatherNameEn: z.string().optional(),
  EmpGrandNameEn: z.string().optional(),
  EmpFamilyNameEn: z.string().optional(),

  // Personal Information - Arabic
  EmpFirstNameAr: z.string().optional(),
  EmpLastNameAr: z.string().optional(),
  EmpSecondNameAr: z.string().optional(),
  EmpFatherNameAr: z.string().optional(),
  EmpGrandNameAr: z.string().optional(),
  EmpFamilyNameAr: z.string().optional(),

  // Contact
  EmpEmail: z.string().email('Invalid email address'),
  EmpMobileNo: z.string().refine(
    (val) => {
      if (!val) return true // Optional
      // +966 for outside, or numbers starting with 5 or 9, total 9 numbers
      const cleaned = val.replace(/\s+/g, '')
      if (cleaned.startsWith('+966')) {
        return cleaned.length === 13 // +966XXXXXXXXX
      }
      if (cleaned.startsWith('5') || cleaned.startsWith('9')) {
        return cleaned.length === 9
      }
      return false
    },
    { message: 'Mobile must be +966XXXXXXXXX or start with 5/9 and be 9 digits' }
  ).optional(),

  // Required fields
  EmpDepartmetId: z.number().min(1, 'Department is required'),
  EmpGenderId: z.number().min(1, 'Gender is required'),
  EmpDob: z.string().min(1, 'Date of birth is required'),
  JoiningDate: z.string().min(1, 'Joining date is required'),

  // Optional fields
  EmpCode: z.string().optional(),
  EmpSectionId: z.number().optional(),
  EmpPostedLocation: z.string().optional(),
  EmpReferenceId: z.string().optional(),
  EmpBirthPlace: z.string().optional(),
  EmpNationalityId: z.number().optional(),
  EmpAge: z.string().optional(),
  EmpAddress: z.string().optional(),
  EmpRefrence: z.string().optional(),
  EmpReferenceContact: z.string().optional(),
  IqamaNo: z.string().optional(),
  IqamaExpiryDate: z.string().optional(),
  PassportNo: z.string().optional(),
  PassportExpiryDate: z.string().optional(),
  OtherNationalityId: z.number().optional(),
  MaritalStatus: z.number().optional(),
  JobId: z.number().optional(),
  CompanyMobileNo: z.string().optional(),
  ProfessionIqama: z.number().optional(),
  ContactPersonNumber: z.string().optional(),
  NoticePeriod: z.number().optional(),
  Status: z.number().optional(),
  IsActive: z.number().optional(),
})

type EmployeeFormData = z.infer<typeof employeeSchema>

/**
 * Employee Edit/Create Page
 * Form for creating new employees or editing existing ones
 */
export const EmployeesEdit: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const { language = 'en' } = useSettingsStore()
  
  const isEditing = Boolean(id)
  const branchId = user?.selectedBranch?.branchId

  const [loading, setLoading] = useState(false)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [selectedImage, setSelectedImage] = useState<File | null>(null)

  // Lookup data
  const [genders, setGenders] = useState<GenderLookup[]>([])
  const [departments, setDepartments] = useState<DepartmentLookup[]>([])
  const [countries, setCountries] = useState<CountryLookup[]>([])
  const [professions, setProfessions] = useState<ProfessionIqamaLookup[]>([])
  const [noticePeriods, setNoticePeriods] = useState<NoticePeriodLookup[]>([])
  const [maritalStatuses, setMaritalStatuses] = useState<MaritalStatusLookup[]>([])
  const [jobs, setJobs] = useState<JobLookup[]>([])

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<EmployeeFormData>({
    resolver: zodResolver(employeeSchema),
    defaultValues: {
      IsActive: 1,
      Status: 1,
    },
  })

  // Load lookup data
  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [gendersData, deptsData, countriesData, professionsData, noticeData, maritalData, jobsData] =
          await Promise.all([
            employeesService.getGenderLookups(),
            employeesService.getDepartmentLookups(),
            employeesService.getCountryLookups(),
            employeesService.getProfessionIqamaLookups(),
            employeesService.getNoticePeriodLookups(),
            employeesService.getMaritalStatusLookups(),
            employeesService.getJobLookups(),
          ])

        console.log('Lookup data loaded:', {
          genders: gendersData,
          departments: deptsData,
          countries: countriesData,
          jobs: jobsData,
        })

        setGenders(gendersData || [])
        setDepartments(deptsData || [])
        setCountries(countriesData || [])
        setProfessions(professionsData || [])
        setNoticePeriods(noticeData || [])
        setMaritalStatuses(maritalData || [])
        setJobs(jobsData || [])
      } catch (error) {
        console.error('Error loading lookups:', error)
        toast.error('Failed to load form data')
      }
    }

    loadLookups()
  }, [])

  // Load employee data for editing
  useEffect(() => {
    if (isEditing && branchId && id) {
      const loadEmployee = async () => {
        try {
          setLoading(true)
          const employee = await employeesService.getEmployeeById(branchId, Number(id))
          
          console.log('Loaded employee data:', employee)
          
          // Map camelCase API fields to PascalCase form fields
          const fieldMap: Record<string, keyof EmployeeFormData> = {
            id: 'Id' as any,
            empCode: 'EmpCode',
            empDepartmetId: 'EmpDepartmetId',
            empSectionId: 'EmpSectionId',
            empPostedLocation: 'EmpPostedLocation',
            empReferenceId: 'EmpReferenceId',
            empFirstNameEn: 'EmpFirstNameEn',
            empSecondNameEn: 'EmpSecondNameEn',
            empLastNameEn: 'EmpLastNameEn',
            empFatherNameEn: 'EmpFatherNameEn',
            empGrandNameEn: 'EmpGrandNameEn',
            empFamilyNameEn: 'EmpFamilyNameEn',
            empFirstNameAr: 'EmpFirstNameAr',
            empSecondNameAr: 'EmpSecondNameAr',
            empLastNameAr: 'EmpLastNameAr',
            empFatherNameAr: 'EmpFatherNameAr',
            empGrandNameAr: 'EmpGrandNameAr',
            empFamilyNameAr: 'EmpFamilyNameAr',
            empEmail: 'EmpEmail',
            empMobileNo: 'EmpMobileNo',
            empDob: 'EmpDob',
            empBirthPlace: 'EmpBirthPlace',
            empNationalityId: 'EmpNationalityId',
            empAge: 'EmpAge',
            empAddress: 'EmpAddress',
            empGenderId: 'EmpGenderId',
            empRefrence: 'EmpRefrence',
            empReferenceContact: 'EmpReferenceContact',
            iqamaNo: 'IqamaNo',
            iqamaExpiryDate: 'IqamaExpiryDate',
            passportNo: 'PassportNo',
            passportExpiryDate: 'PassportExpiryDate',
            otherNationalityId: 'OtherNationalityId',
            maritalStatus: 'MaritalStatus',
            jobId: 'JobId',
            companyMobileNo: 'CompanyMobileNo',
            professionIqama: 'ProfessionIqama',
            contactPersonNumber: 'ContactPersonNumber',
            noticePeriod: 'NoticePeriod',
            status: 'Status',
            joiningDate: 'JoiningDate',
            isActive: 'IsActive',
          }

          // Set form values using mapping
          Object.entries(employee).forEach(([key, value]) => {
            const formField = fieldMap[key]
            if (formField && value !== undefined && value !== null && value !== '') {
              setValue(formField, value as any)
            }
          })

          // Set image preview if exists
          if (employee.imageUrl || employee.profileImageUrl) {
            setImagePreview(employee.imageUrl || employee.profileImageUrl || null)
          }
        } catch (error: any) {
          console.error('Error loading employee:', error)
          toast.error(error.response?.data?.message || 'Failed to load employee data')
          navigate('/employees')
        } finally {
          setLoading(false)
        }
      }

      loadEmployee()
    }
  }, [isEditing, branchId, id, setValue, navigate])

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedImage(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const onSubmit = async (data: EmployeeFormData) => {
    if (!branchId) {
      toast.error('Please select a branch first')
      return
    }

    try {
      setLoading(true)

      // Create FormData for multipart/form-data
      const formData = new FormData()

      // Add all form fields
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          formData.append(key, String(value))
        }
      })

      // Add branch and company IDs
      formData.append('BranchId', String(branchId))
      if (user?.userId) {
        formData.append('CreatedById', String(user.userId))
        formData.append('UpdatedById', String(user.userId))
      }

      // Add image if selected
      if (selectedImage) {
        formData.append('profileImage', selectedImage)
      }

      // Add ID for update
      if (isEditing && id) {
        formData.append('Id', id)
      }

      let response
      if (isEditing) {
        response = await employeesService.updateEmployee(formData)
      } else {
        response = await employeesService.saveEmployee(formData)
      }

      if (response.success) {
        toast.success(isEditing ? 'Employee updated successfully' : 'New record has been saved')
        navigate('/employees')
      } else {
        toast.error(response.message || 'Failed to save employee')
      }
    } catch (error: any) {
      console.error('Error saving employee:', error)
      toast.error(error.response?.data?.message || 'Failed to save employee')
    } finally {
      setLoading(false)
    }
  }

  const getEmployeeInitials = () => {
    const firstName = watch('EmpFirstNameEn') || watch('EmpFirstNameAr') || ''
    const lastName = watch('EmpLastNameEn') || watch('EmpLastNameAr') || ''
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'NA'
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <PageHeader
          title={isEditing ? 'Edit Employee' : 'Add New Employee'}
          description={isEditing ? 'Update employee information' : 'Add a new employee to your organization'}
        />
        <Card>
          <CardContent className="p-6">
            <p className="text-muted-foreground">Please select a branch first</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={isEditing ? 'Edit Employee' : 'Add New Employee'}
        description={isEditing ? 'Update employee information' : 'Add a new employee to your organization'}
      />

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-6">
          {/* Profile Image */}
      <Card>
        <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Profile Photo
              </CardTitle>
        </CardHeader>
        <CardContent>
              <div className="flex items-center gap-6">
                <Avatar className="h-24 w-24">
                  <AvatarImage src={imagePreview || undefined} alt="Profile" />
                  <AvatarFallback className="text-2xl">{getEmployeeInitials()}</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <Label htmlFor="profileImage">Upload Profile Image</Label>
                  <Input
                    id="profileImage"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="mt-2"
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Select an image file for the employee profile
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Personal Information - English */}
          <Card>
            <CardHeader>
              <CardTitle>Personal Information (English)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="EmpFirstNameEn">
                    First Name (English) <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="EmpFirstNameEn"
                    {...register('EmpFirstNameEn')}
                    className={errors.EmpFirstNameEn ? 'border-destructive' : ''}
                  />
                  {errors.EmpFirstNameEn && (
                    <p className="text-sm text-destructive mt-1">{errors.EmpFirstNameEn.message}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="EmpSecondNameEn">Second Name (English)</Label>
                  <Input id="EmpSecondNameEn" {...register('EmpSecondNameEn')} />
                </div>
                <div>
                  <Label htmlFor="EmpLastNameEn">
                    Last Name (English) <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="EmpLastNameEn"
                    {...register('EmpLastNameEn')}
                    className={errors.EmpLastNameEn ? 'border-destructive' : ''}
                  />
                  {errors.EmpLastNameEn && (
                    <p className="text-sm text-destructive mt-1">{errors.EmpLastNameEn.message}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="EmpFatherNameEn">Father Name (English)</Label>
                  <Input id="EmpFatherNameEn" {...register('EmpFatherNameEn')} />
                </div>
                <div>
                  <Label htmlFor="EmpGrandNameEn">Grand Name (English)</Label>
                  <Input id="EmpGrandNameEn" {...register('EmpGrandNameEn')} />
                </div>
                <div>
                  <Label htmlFor="EmpFamilyNameEn">Family Name (English)</Label>
                  <Input id="EmpFamilyNameEn" {...register('EmpFamilyNameEn')} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Personal Information - Arabic */}
          <Card>
            <CardHeader>
              <CardTitle>Personal Information (Arabic)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="EmpFirstNameAr">First Name (Arabic)</Label>
                  <Input id="EmpFirstNameAr" {...register('EmpFirstNameAr')} />
                </div>
                <div>
                  <Label htmlFor="EmpSecondNameAr">Second Name (Arabic)</Label>
                  <Input id="EmpSecondNameAr" {...register('EmpSecondNameAr')} />
                </div>
                <div>
                  <Label htmlFor="EmpLastNameAr">Last Name (Arabic)</Label>
                  <Input id="EmpLastNameAr" {...register('EmpLastNameAr')} />
                </div>
                <div>
                  <Label htmlFor="EmpFatherNameAr">Father Name (Arabic)</Label>
                  <Input id="EmpFatherNameAr" {...register('EmpFatherNameAr')} />
                </div>
                <div>
                  <Label htmlFor="EmpGrandNameAr">Grand Name (Arabic)</Label>
                  <Input id="EmpGrandNameAr" {...register('EmpGrandNameAr')} />
                </div>
                <div>
                  <Label htmlFor="EmpFamilyNameAr">Family Name (Arabic)</Label>
                  <Input id="EmpFamilyNameAr" {...register('EmpFamilyNameAr')} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact & Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Contact & Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="EmpEmail">
                    Email <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="EmpEmail"
                    type="email"
                    {...register('EmpEmail')}
                    className={errors.EmpEmail ? 'border-destructive' : ''}
                  />
                  {errors.EmpEmail && (
                    <p className="text-sm text-destructive mt-1">{errors.EmpEmail.message}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="EmpMobileNo">Mobile Number</Label>
                  <Input
                    id="EmpMobileNo"
                    {...register('EmpMobileNo')}
                    placeholder="+966XXXXXXXXX or 5XXXXXXXX"
                    className={errors.EmpMobileNo ? 'border-destructive' : ''}
                  />
                  {errors.EmpMobileNo && (
                    <p className="text-sm text-destructive mt-1">{errors.EmpMobileNo.message}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="EmpDob">
                    Date of Birth <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="EmpDob"
                    type="date"
                    {...register('EmpDob')}
                    className={errors.EmpDob ? 'border-destructive' : ''}
                  />
                  {errors.EmpDob && (
                    <p className="text-sm text-destructive mt-1">{errors.EmpDob.message}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="EmpAge">Age</Label>
                  <Input id="EmpAge" {...register('EmpAge')} />
                </div>
                <div>
                  <SearchableSelect
                    label={
                      <>
                        Gender <span className="text-destructive">*</span>
                      </>
                    }
                    options={(genders || []).map((gender) => ({
                      value: String(gender.id),
                      label: language === 'ar' ? (gender.lookupNameAr || gender.lookupNameEn || '') : (gender.lookupNameEn || gender.lookupNameAr || ''),
                    }))}
                    value={watch('EmpGenderId')?.toString() || ''}
                    onValueChange={(value) => setValue('EmpGenderId', Number(value))}
                    placeholder="Select gender"
                    searchPlaceholder="Search gender..."
                    emptyMessage="No gender found"
                    error={!!errors.EmpGenderId}
                    errorMessage={errors.EmpGenderId?.message}
                  />
                </div>
                <div>
                  <Label htmlFor="EmpBirthPlace">Birth Place</Label>
                  <Input id="EmpBirthPlace" {...register('EmpBirthPlace')} />
                </div>
                <div>
                  <SearchableSelect
                    label="Nationality"
                    options={(countries || []).map((country) => ({
                      value: String(country.id),
                      label: language === 'ar' ? (country.lookupNameAr || country.lookupNameEn || '') : (country.lookupNameEn || country.lookupNameAr || ''),
                    }))}
                    value={watch('EmpNationalityId')?.toString() || ''}
                    onValueChange={(value) => setValue('EmpNationalityId', Number(value))}
                    placeholder="Select nationality"
                    searchPlaceholder="Search nationality..."
                    emptyMessage="No nationality found"
                  />
                </div>
                <div>
                  <SearchableSelect
                    label="Marital Status"
                    options={(maritalStatuses || []).map((status) => ({
                      value: String(status.id),
                      label: language === 'ar' ? (status.lookupNameAr || status.lookupNameEn || '') : (status.lookupNameEn || status.lookupNameAr || ''),
                    }))}
                    value={watch('MaritalStatus')?.toString() || ''}
                    onValueChange={(value) => setValue('MaritalStatus', Number(value))}
                    placeholder="Select marital status"
                    searchPlaceholder="Search marital status..."
                    emptyMessage="No marital status found"
                  />
                </div>
                <div className="md:col-span-3">
                  <Label htmlFor="EmpAddress">Address</Label>
                  <Input id="EmpAddress" {...register('EmpAddress')} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Employment Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="w-5 h-5" />
                Employment Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="EmpCode">Employee Code</Label>
                  <Input id="EmpCode" {...register('EmpCode')} />
                </div>
                <div>
                  <SearchableSelect
                    label={
                      <>
                        Department <span className="text-destructive">*</span>
                      </>
                    }
                    options={(departments || []).map((dept) => ({
                      value: String(dept.id),
                      label: language === 'ar' ? (dept.lookupNameAr || dept.lookupNameEn || '') : (dept.lookupNameEn || dept.lookupNameAr || ''),
                    }))}
                    value={watch('EmpDepartmetId')?.toString() || ''}
                    onValueChange={(value) => setValue('EmpDepartmetId', Number(value))}
                    placeholder="Select department"
                    searchPlaceholder="Search department..."
                    emptyMessage="No department found"
                    error={!!errors.EmpDepartmetId}
                    errorMessage={errors.EmpDepartmetId?.message}
                  />
                </div>
                <div>
                  <Label htmlFor="EmpSectionId">Section</Label>
                  <Select
                    value={watch('EmpSectionId')?.toString() || undefined}
                    onValueChange={(value) => setValue('EmpSectionId', Number(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select section" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0" disabled>No sections available</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <SearchableSelect
                    label="Job Title"
                    options={(jobs || []).map((job) => ({
                      value: String(job.id),
                      label: language === 'ar' ? (job.lookupNameAr || job.lookupNameEn || '') : (job.lookupNameEn || job.lookupNameAr || ''),
                    }))}
                    value={watch('JobId')?.toString() || ''}
                    onValueChange={(value) => setValue('JobId', Number(value))}
                    placeholder="Select job"
                    searchPlaceholder="Search job..."
                    emptyMessage="No job found"
                  />
                </div>
                <div>
                  <Label htmlFor="JoiningDate">
                    Joining Date <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="JoiningDate"
                    type="date"
                    {...register('JoiningDate')}
                    className={errors.JoiningDate ? 'border-destructive' : ''}
                  />
                  {errors.JoiningDate && (
                    <p className="text-sm text-destructive mt-1">{errors.JoiningDate.message}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="EmpPostedLocation">Posted Location</Label>
                  <Input id="EmpPostedLocation" {...register('EmpPostedLocation')} />
                </div>
                <div>
                  <SearchableSelect
                    label="Notice Period"
                    options={(noticePeriods || []).map((period) => ({
                      value: String(period.id),
                      label: language === 'ar' ? (period.lookupNameAr || period.lookupNameEn || '') : (period.lookupNameEn || period.lookupNameAr || ''),
                    }))}
                    value={watch('NoticePeriod')?.toString() || ''}
                    onValueChange={(value) => setValue('NoticePeriod', Number(value))}
                    placeholder="Select notice period"
                    searchPlaceholder="Search notice period..."
                    emptyMessage="No notice period found"
                  />
                </div>
                <div>
                  <Label htmlFor="CompanyMobileNo">Company Mobile</Label>
                  <Input id="CompanyMobileNo" {...register('CompanyMobileNo')} />
                </div>
                <div>
                  <Label htmlFor="ContactPersonNumber">Contact Person Number</Label>
                  <Input id="ContactPersonNumber" {...register('ContactPersonNumber')} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Documents */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Documents
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="IqamaNo">Iqama Number</Label>
                  <Input id="IqamaNo" {...register('IqamaNo')} />
                </div>
                <div>
                  <Label htmlFor="IqamaExpiryDate">Iqama Expiry Date</Label>
                  <Input id="IqamaExpiryDate" type="date" {...register('IqamaExpiryDate')} />
                </div>
                <div>
                  <Label htmlFor="PassportNo">Passport Number</Label>
                  <Input id="PassportNo" {...register('PassportNo')} />
                </div>
                <div>
                  <Label htmlFor="PassportExpiryDate">Passport Expiry Date</Label>
                  <Input id="PassportExpiryDate" type="date" {...register('PassportExpiryDate')} />
                </div>
                <div>
                  <SearchableSelect
                    label="Profession in Iqama"
                    options={(professions || []).map((prof) => ({
                      value: String(prof.id),
                      label: language === 'ar' ? (prof.lookupNameAr || prof.lookupNameEn || '') : (prof.lookupNameEn || prof.lookupNameAr || ''),
                    }))}
                    value={watch('ProfessionIqama')?.toString() || ''}
                    onValueChange={(value) => setValue('ProfessionIqama', Number(value))}
                    placeholder="Select profession"
                    searchPlaceholder="Search profession..."
                    emptyMessage="No profession found"
                  />
                </div>
                <div>
                  <SearchableSelect
                    label="Other Nationality"
                    options={(countries || []).map((country) => ({
                      value: String(country.id),
                      label: language === 'ar' ? (country.lookupNameAr || country.lookupNameEn || '') : (country.lookupNameEn || country.lookupNameAr || ''),
                    }))}
                    value={watch('OtherNationalityId')?.toString() || ''}
                    onValueChange={(value) => setValue('OtherNationalityId', Number(value))}
                    placeholder="Select nationality"
                    searchPlaceholder="Search nationality..."
                    emptyMessage="No nationality found"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Reference Information */}
          <Card>
            <CardHeader>
              <CardTitle>Reference Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="EmpReferenceId">Reference ID</Label>
                  <Input id="EmpReferenceId" {...register('EmpReferenceId')} />
                </div>
                <div>
                  <Label htmlFor="EmpRefrence">Reference</Label>
                  <Input id="EmpRefrence" {...register('EmpRefrence')} />
                </div>
                <div>
                  <Label htmlFor="EmpReferenceContact">Reference Contact</Label>
                  <Input id="EmpReferenceContact" {...register('EmpReferenceContact')} />
                </div>
          </div>
        </CardContent>
      </Card>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/employees')} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="w-full sm:w-auto">
              {loading ? (
                <>
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isEditing ? 'Update Employee' : 'Save Employee'}
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}
